import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Landmark, Download, Users, FileSpreadsheet } from 'lucide-react';
import { isoDate } from '../../utils/time';
import { PageHead, Panel, Register, FigureStrip, Figure, Currency, ErrorState, fmtDate } from './design';

// Government financial reports: the Support at Home figures for the Quarterly Financial Report
// (GPMS, four times a year) and the Aged Care Financial Report (by 31 October).
//
// The QFR asks for the year-to-date income statement segmented to Support at Home and the rates of
// pay of direct care employees by category; the ACFR asks for the year's income from services,
// subsidies and supplements, and expenses including wages. This page gathers what the system holds
// so it can be keyed in. It is a worksheet: the viability questions, the balance sheet and costs
// kept outside this system are the provider's own.

const STREAMS = [
  ['ongoing', 'Services from the quarterly budget'],
  ['careManagement', 'Care management'],
  ['restorative', 'Restorative Care Pathway'],
  ['endOfLife', 'End-of-Life Pathway'],
  ['athm', 'Assistive technology and home modifications'],
];

// The financial year a date falls in (July to June), and the quarters in it.
const fyOf = (d) => { const y = Number(d.slice(0, 4)); return Number(d.slice(5, 7)) >= 7 ? y : y - 1; };
function periodOptions() {
  const fy = fyOf(isoDate());
  const out = [];
  for (const y of [fy, fy - 1]) {
    const label = `${y}-${String(y + 1).slice(2)}`;
    out.push([`${y}-Q1`, `QFR ${label} Q1 (Jul to Sep)`], [`${y}-Q2`, `QFR ${label} Q2 (Oct to Dec)`],
      [`${y}-Q3`, `QFR ${label} Q3 (Jan to Mar)`], [`${y}-Q4`, `QFR ${label} Q4 (Apr to Jun)`], [`${y}-FY`, `ACFR ${label} (whole year)`]);
  }
  return out;
}
// The report most likely wanted now: the last quarter that has finished.
function defaultPeriod() {
  const t = isoDate(); const m = Number(t.slice(5, 7)); const fy = fyOf(t);
  if (m >= 7 && m <= 9) return `${fy - 1}-Q4`;
  if (m >= 10) return `${fy}-Q1`;
  if (m <= 3) return `${fy}-Q2`;
  return `${fy}-Q3`;
}

function IncomeTable({ x, title }) {
  if (!x) return null;
  return (
    <Panel title={title} bodyClass="px-4 py-2">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-[11px] text-slate-500 text-left">
            <th className="py-1.5 font-medium">Stream</th>
            <th className="py-1.5 font-medium text-right">Price of services</th>
            <th className="py-1.5 font-medium text-right">Government share</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {STREAMS.map(([k, label]) => (
            <tr key={k}>
              <td className="py-1.5 text-slate-700">{label}</td>
              <td className="py-1.5 text-right"><Currency value={x[k]?.price} /></td>
              <td className="py-1.5 text-right"><Currency value={x[k]?.government} /></td>
            </tr>
          ))}
          <tr><td className="py-1.5 text-slate-700">Participant contributions</td><td /><td className="py-1.5 text-right"><Currency value={x.contributions} /></td></tr>
          <tr className="font-semibold text-slate-900"><td className="py-1.5">Total revenue</td><td /><td className="py-1.5 text-right"><Currency value={x.totalRevenue} /></td></tr>
        </tbody>
      </table>
      {x.estimated > 0.005 && (
        <p className="text-[12px] text-amber-800 mt-2">
          <Currency value={x.estimated} /> of the government share is still our estimate: Services Australia has not paid those claims yet. Import the payment statements in Claims before you lodge.
        </p>
      )}
    </Panel>
  );
}

export default function AgedCareGovReports() {
  const [period, setPeriod] = useState(defaultPeriod());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [savingFor, setSavingFor] = useState(null); // the worker whose category is being saved

  // A failed build says so on the page, with a retry, rather than leaving the page blank.
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try { const r = await api.get('/aged-care/reports/financial', { params: { period } }); setData(r.data); }
    catch (e) { setLoadError(e.response?.data?.message || 'Could not build the report'); setData(null); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const r = await api.get('/aged-care/reports/financial', { params: { period, format: 'csv' }, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `${period.endsWith('FY') ? 'acfr' : 'qfr'}-support-at-home-${period}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // A blob response hides the server's JSON message; read it back out.
      let msg = 'Could not download the worksheet';
      try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* not JSON */ }
      toast.error(msg);
    }
  };
  // `category` null goes back to the category worked out from the worker's qualifications.
  const setCategory = async (w, category) => {
    setSavingFor(w.id);
    try {
      await api.patch('/aged-care/reports/worker-category', { userId: w.id, category });
      toast.success(category ? `${w.name} is counted as ${cats[category] || 'the chosen category'}` : `${w.name} is back to the automatic category`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the category'); } finally { setSavingFor(null); }
  };

  const per = data?.period;
  const cats = data?.categories || {};
  const labourCols = [
    { key: 'label', header: 'Category', render: (b) => <span className="font-medium text-slate-900">{b.label}</span> },
    { key: 'employeeHours', header: 'Employee hours', align: 'right', render: (b) => <span className="tabular-nums">{Number(b.employeeHours).toFixed(2)}</span> },
    { key: 'employeeCost', header: 'Employee cost', align: 'right', render: (b) => <Currency value={b.employeeCost} /> },
    { key: 'averageRate', header: 'Average hourly rate', align: 'right', render: (b) => (b.averageRate == null ? <span className="text-slate-400">—</span> : <Currency value={b.averageRate} />) },
    { key: 'contractorHours', header: 'Contractor hours', align: 'right', render: (b) => <span className="tabular-nums">{Number(b.contractorHours).toFixed(2)}</span> },
    { key: 'contractorCost', header: 'Contractor cost', align: 'right', render: (b) => <Currency value={b.contractorCost} /> },
  ];
  const rateCols = [
    { key: 'label', header: 'Category', render: (b) => <span className="font-medium text-slate-900">{b.label}</span> },
    { key: 'averageRate', header: 'Average hourly rate', align: 'right', render: (b) => (b.averageRate == null ? <span className="text-slate-400">—</span> : <Currency value={b.averageRate} />) },
    { key: 'employees', header: 'Employees', align: 'right', render: (b) => <span className="tabular-nums">{b.employees}</span> },
    { key: 'employeeHours', header: 'Employee hours', align: 'right', render: (b) => <span className="tabular-nums">{Number(b.employeeHours).toFixed(2)}</span> },
  ];
  const workerCols = [
    { key: 'name', header: 'Worker', sortable: true, render: (w) => <span className="text-slate-900">{w.name}{w.contractor ? <span className="text-[11px] text-slate-500"> (contractor)</span> : null}</span> },
    // A worker not yet confirmed sits on "Automatic", which names the category worked out from their
    // qualifications. Picking that same category in the list, or pressing Confirm, makes it theirs;
    // picking Automatic again hands it back to the qualifications.
    {
      key: 'category', header: 'Counted as', width: 300, render: (w) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <select aria-label={`Category for ${w.name}`} value={w.categorySet ? w.category : ''} disabled={savingFor === w.id}
            onChange={(e) => setCategory(w, e.target.value || null)}
            className={`border rounded-lg px-2 py-1 text-[12px] ${w.categorySet ? 'border-slate-300' : 'border-amber-300 bg-amber-50'}`}>
            <option value="">{`Automatic${!w.categorySet && cats[w.category] ? `: ${cats[w.category]}` : ''}`}</option>
            {Object.entries(cats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {!w.categorySet && w.category && (
            <button type="button" className="btn-ghost btn-sm" disabled={savingFor === w.id} onClick={() => setCategory(w, w.category)}
              aria-label={`Confirm ${w.name} as ${cats[w.category] || 'the suggested category'}`}>Confirm</button>
          )}
        </span>
      ),
    },
    { key: 'hours', header: 'Hours', align: 'right', render: (w) => <span className="tabular-nums">{Number(w.hours).toFixed(2)}</span> },
    { key: 'pay', header: 'Pay', align: 'right', render: (w) => <Currency value={w.pay} /> },
    { key: 'rate', header: 'Hourly', align: 'right', render: (w) => (w.rate == null ? '—' : <Currency value={w.rate} />) },
  ];

  return (
    <div>
      <PageHead icon={FileSpreadsheet}
        title="Government reports"
        purpose="The Support at Home figures for the Quarterly Financial Report (lodged in GPMS) and the Aged Care Financial Report. A worksheet to key in from: the viability questions and your balance sheet are not in here."
        actions={(
          <>
            <select aria-label="Report period" value={period} onChange={(e) => setPeriod(e.target.value)} className="input-field !w-auto !rounded-lg !py-1.5 !text-[13px]">
              {periodOptions().map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button type="button" onClick={download} className="btn-secondary"><Download size={15} /> Download worksheet</button>
          </>
        )}
      />

      {per && (
        <FigureStrip>
          <Figure label="Report" value={per.report === 'Aged Care Financial Report' ? 'ACFR' : `QFR ${per.which}`} sub={`${fmtDate(per.from)} to ${fmtDate(per.to)}`} />
          <Figure label="Due" value={fmtDate(per.due)} sub={per.report === 'Aged Care Financial Report' ? 'Forms portal' : 'GPMS'} />
          <Figure label="Revenue this period" value={<Currency value={data?.income?.totalRevenue} />} />
          <Figure label="Paid by Services Australia" value={<Currency value={data?.cashReceivedFromServicesAustralia} />} sub="Payment statements dated in the period" />
        </FigureStrip>
      )}

      {loading && !data && !loadError && <p className="text-[13px] text-slate-500">Building the report…</p>}

      {loadError && !loading && (
        <Panel><ErrorState message={loadError} onRetry={load} /></Panel>
      )}

      {data && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <IncomeTable x={data.income} title={per?.which === 'FY' ? 'Income for the year' : 'Income for the quarter'} />
            {data.incomeYtd && <IncomeTable x={data.incomeYtd} title="Income year to date (the QFR asks for this)" />}
          </div>

          {data.form === 'rates_of_pay' ? (
            <Register
              title="Support at Home rates of pay" count={(data.ratesOfPay || []).filter((b) => b.employees).length} countLabel="categories" icon={Landmark}
              columns={rateCols} rows={data.ratesOfPay || []} rowKey="category"
              footer="From Q1 2026-27 the QFR no longer asks for labour costs. It asks for the average hourly rate of direct care employees in each category, worked out here from approved timesheets on aged care visits. Contractors are not included."
            />
          ) : (
            <Register
              title="Direct care labour on Support at Home visits" count={(data.labour || []).filter((b) => b.people).length} countLabel="categories" icon={Landmark}
              columns={labourCols} rows={data.labour || []} rowKey="category"
              footer="From approved timesheets on aged care visits, by category: registered nurses, enrolled nurses, personal care workers and other direct care workers."
            />
          )}

          <Register
            title="Workers in this period" count={(data.workers || []).length} countLabel="workers" icon={Users}
            columns={workerCols} rows={data.workers || []} rowKey="id"
            rowTone={(w) => (w.categorySet ? undefined : 'amber')}
            empty={{ icon: Users, title: 'No approved timesheets on aged care visits in this period' }}
            footer="A category in amber was worked out from the worker's qualifications. Press Confirm to keep it, or choose another; either is remembered for every report. Choose Automatic to go back to the qualifications."
          />
        </div>
      )}
    </div>
  );
}
